import axios from "axios";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactTable from "../../../../components/ReactTable";
import { IoClose } from "react-icons/io5";
import { FiLoader } from "react-icons/fi";
import { createColumnHelper } from "@tanstack/react-table";
import { CiEdit } from "react-icons/ci";
import { GoTrash, GoEye } from "react-icons/go";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";

function LearningVideoViewPlaylist() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showEntries, setShowEntries] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [newCategory, setNewCategory] = useState({
    title: "",
    file: null,
    isStared: false,
  });
  const [editCategory, setEditCategory] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [viewVideo, setViewVideo] = useState(null);
  const [videoDeleted, setVideoDeleted] = useState(false);

  const { token,authToken } = useSelector((state) => state.login);
  const columnHelper = createColumnHelper();

  const base = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_TYPE}`;

  // ✅ Debounce search input
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(1);
    }, 600);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // ✅ Fetch playlist
  const fetchPublicCollaterals = async (page = 1, limit = 10, query = "") => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${base}/learningVideos/${location?.state?.id}/playlist/${params?.id}?page=${page}&limit=${limit}&search=${query}`,
        {
          headers: { Authorization: `Bearer ${authToken || token}` },
        }
      );
      const data = response?.data?.data;
      setCategories(data?.list || []);
      setTotalPages(data?.totalPages || 1);
      setCurrentPage(data?.currentPage || 1);
    } catch (error) {
      console.error("Error fetching playlist:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicCollaterals(currentPage, showEntries, searchQuery);
  }, [currentPage, showEntries, searchQuery]);

  // 🧩 File change handler
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (filePreview) URL.revokeObjectURL(filePreview); // cleanup old preview
      setNewCategory((prev) => ({ ...prev, file }));
      setFilePreview(URL.createObjectURL(file));
      setVideoDeleted(false);
    }
  };

  // 🧩 Input changes
  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setNewCategory((prev) => ({ ...prev, [name]: value }));
  };
  const handleEditCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setEditCategory((prev) => ({ ...prev, "name": value }));
  };

  // 🟢 Toggle handler
  const handleToggleChange = (isEdit = false) => {
    if (isEdit) {
      setEditCategory((prev) => ({ ...prev, isStared: !prev.isStared }));
    } else {
      setNewCategory((prev) => ({ ...prev, isStared: !prev.isStared }));
    }
  };

  // 🧹 Reset modal state (used for closing)
  const handleCloseModal = () => {
    // revoke object URL to free memory
    if (filePreview) URL.revokeObjectURL(filePreview);

    setIsModalOpen(false);
    setEditCategory(null);
    setVideoDeleted(false);
    setFilePreview(null);
    setNewCategory({ title: "", file: null, isStared: false });
  };

  // Clean up previews if component unmounts
  useEffect(() => {
    return () => {
      if (filePreview) URL.revokeObjectURL(filePreview);
    };
  }, [filePreview]);

  // ➕ Create New Video
  const handleCreateCategory = async () => {
    if (!newCategory.title || !newCategory.file) {
      toast.info("Please enter a title and upload a video.");
      return;
    }

    const formData = new FormData();
    formData.append("name", newCategory.title);
    formData.append("file", newCategory.file);
    formData.append("isStared", newCategory.isStared);

    setModalLoading(true);
    try {
      await axios.post(
        `${base}/learningVideos/${location?.state.id}/playlist/${params.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken || token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      fetchPublicCollaterals();
      toast.success("Video added successfully!");
      handleCloseModal();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error adding video.");
    } finally {
      setModalLoading(false);
    }
  };

  // ✏️ Edit Video
  const handleEditCategorySubmit = async () => {
    if (!editCategory?.name) {
      toast.info("Please enter a title.");
      return;
    }

    if (videoDeleted && !newCategory?.file) {
      toast.info("Please upload a new video before saving.");
      return;
    }

    const formData = new FormData();
    formData.append("name", editCategory.name);
    formData.append("isStared", editCategory.isStared);

    if (!videoDeleted) {
      formData.append("file", newCategory?.file || editCategory?.file || "");
    } else {
      formData.append("file", newCategory?.file);
    }

    setModalLoading(true);
    try {
      await axios.put(
        `${base}/learningVideos/${location.state.id}/playlist/${params.id}/${editCategory?.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken || token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Video updated successfully!");
      handleCloseModal();
      fetchPublicCollaterals();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error updating video.");
    } finally {
      setModalLoading(false);
    }
  };

  // 🗑️ Delete Playlist Item
  const handleDeleteAddets = async () => {
    setLoading(true);
    try {
      if (categoryToDelete) {
        await axios.delete(`${base}/learningVideos/${location.state.id}/playlist/${params.id}/${categoryToDelete}`, {
          headers: { Authorization: `Bearer ${authToken || token}` },
        });
        toast.success("Video deleted successfully!");
        fetchPublicCollaterals();
        setIsDeleteModalOpen(false);
        setCategoryToDelete(null);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error deleting video.");
    } finally {
      setLoading(false);
    }
  };

  // 🧩 Table Columns
  const hostedcolumns = [
    columnHelper.accessor("name", {
      id: "Name",
      header: () => <span>Name</span>,
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("isStared", {
      id: "Starred",
      header: () => <span>Starred</span>,
      cell: (info) => (
        <span
          className={`font-medium ${
            info.getValue() ? "text-green-600" : "text-gray-500"
          }`}
        >
          {info.getValue() ? "Yes" : "No"}
        </span>
      ),
    }),
    columnHelper.accessor("videoUrl", {
      id: "videoUrl",
      header: () => <span>Video</span>,
      cell: (info) => {
                  const category = info.row.original;
        return(

          <button
          title="View Video"
          onClick={() => setViewVideo(category.videoUrl)}
          className="text-blue-600 hover:text-blue-800"
          >
             View
            </button>
            )
      
    }
    }),
    columnHelper.accessor((row) => row, {
      id: "action",
      header: () => (
        <div className="flex justify-end">
          <span>Action</span>
        </div>
      ),
      cell: (info) => {
        const category = info.row.original;
        return (
          <div className="flex justify-end gap-5 text-lg">
            {/* <button
              title="View Video"
              onClick={() => setViewVideo(category.videoUrl)}
              className="text-blue-600 hover:text-blue-800"
            >
              <GoEye />
            </button> */}
            <button
              title="Edit"
              onClick={() => {
                setEditCategory(category);
                setIsModalOpen(true);
                setVideoDeleted(false);
                setFilePreview(null);
              }}
              className="text-green-600 hover:text-green-800"
            >
              <CiEdit />
            </button>
            <button
              title="Delete"
              onClick={() => {
                setCategoryToDelete(category.id);
                setIsDeleteModalOpen(true);
              }}
              className="text-red-600 hover:text-red-800"
            >
              <GoTrash />
            </button>
          </div>
        );
      },
    }),
  ];

  const Loader = () => (
    <div className="flex justify-center items-center py-10">
      <FiLoader className="animate-spin text-4xl text-blue-600" />
    </div>
  );

  const handleBack = () => navigate(-1);

  return (
    <div className="mt-[100px] p-6">
      <div className="w-full pl-5 pt-5">
        <span onClick={handleBack} className="text-[#065697] cursor-pointer">
          Back
        </span>
      </div>

      {/* 🧩 Table and Controls */}
      <div className="p-6 bg-gray-50 rounded-lg mt-[50px]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">
            Learning Video Playlist
          </h2>

          <input
            type="text"
            placeholder="Search videos..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="border px-3 py-2 rounded-md w-1/3"
          />

          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-[#065697] text-white px-4 py-2 rounded-md transition"
          >
            + Add New
          </button>
        </div>

        {loading ? (
          <Loader />
        ) : categories?.length > 0 ? (
          <>
            <ReactTable defaultData={categories} columns={hostedcolumns} />
            <div className="flex justify-between items-center mt-6">
              <p>
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-3">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="px-4 py-2 border rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="px-4 py-2 border rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <p className="text-center text-gray-500 py-10">No videos found.</p>
        )}
      </div>

      {/* ➕ / ✏️ Modal */}
      {(isModalOpen || editCategory) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-1/3 relative">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                {editCategory ? "Edit Video" : "Add New Video"}
              </h3>
              <IoClose
                className="cursor-pointer text-xl"
                onClick={handleCloseModal}
              />
            </div>

            {/* Title */}
            {console.log(editCategory)}
            <input
              type="text"
              name="title"
              value={editCategory ? editCategory.name : newCategory.title}
              onChange={
                editCategory
                  ? handleEditCategoryInputChange
                  : handleCategoryInputChange
              }
              placeholder="Video Title"
              className="p-3 border rounded-md w-full mb-4"
            />

            {/* ⭐ isStared Toggle */}
            <div className="flex items-center gap-3 mb-4">
              <label className="font-medium text-gray-700">Starred:</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    editCategory
                      ? editCategory.isStared || false
                      : newCategory.isStared
                  }
                  onChange={() => handleToggleChange(!!editCategory)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* File Upload */}
            {editCategory && editCategory.videoUrl && !videoDeleted ? (
              <div className="relative mb-4">
                <video
                  src={editCategory.videoUrl}
                  controls
                  className="w-full rounded-md border"
                />
                <button
                  onClick={() => {
                    setVideoDeleted(true);
                    setEditCategory((prev) => ({ ...prev, videoUrl: null }));
                    setNewCategory((prev) => ({ ...prev, file: null }));
                    toast.info("Video deleted, please upload a new one.");
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2"
                >
                  <GoTrash />
                </button>
              </div>
            ) : (
              <div className="mb-4">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  className="p-3 border rounded-md w-full"
                />
                {filePreview && (
                  <video
                    src={filePreview}
                    controls
                    className="w-full rounded-md border mt-3"
                  />
                )}
            <span>Only video allowed</span>
              </div>
            )}

            {modalLoading ? (
              <Loader />
            ) : (
              <button
                onClick={editCategory ? handleEditCategorySubmit : handleCreateCategory}
                className="bg-[#065697] text-white px-4 py-2 rounded-md w-full"
              >
                {editCategory ? "Update Video" : "Add Video"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* 🗑️ Delete Confirmation */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <h3 className="text-xl font-semibold">
              Are you sure you want to delete this video?
            </h3>
            <div className="mt-4 flex justify-end gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="bg-gray-300 text-black px-4 py-2 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAddets}
                disabled={loading}
                className="bg-red-600 text-white px-4 py-2 rounded-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🎬 Video Modal */}
      {viewVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-[700px] relative">
            <IoClose
              className="absolute top-1 right-2 text-2xl cursor-pointer"
              onClick={() => setViewVideo(null)}
            />
            <video src={viewVideo} controls autoPlay className="w-full rounded-md" />
          </div>
        </div>
      )}
    </div>
  );
}

export default LearningVideoViewPlaylist;
